(function () {
  if (customElements.get("hypemat-graphic-check")) return;

  /*
   * Paid graphic-check add-on card below the custom uploader.
   *
   * Ported from the old theme's product-form snippet with a deliberate behaviour
   * change: the checkbox only records intent. The add-on is added to the cart
   * AFTER the main product was successfully added (the product form's
   * "variant:added" event) — the old immediate-add-on-toggle left orphaned
   * add-ons in the cart and desynced after a page reload. A cart lookup before
   * adding keeps the add-on at max. one line no matter how often the customer
   * re-adds or reloads.
   */
  class HypematGraphicCheck extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.form = document.getElementById(this.dataset.formId);
      this.checkbox = this.querySelector("[data-graphic-check-input]");
      this.moreButton = this.querySelector("[data-graphic-check-more]");
      this.moreLabel = this.querySelector("[data-graphic-check-more-label]");
      this.details = this.querySelector("[data-graphic-check-details]");
      this.status = this.querySelector("[data-graphic-check-status]");
      this.addInFlight = false;

      this.onVariantAdded = this.handleVariantAdded.bind(this);
      this.onToggleDetails = this.handleToggleDetails.bind(this);

      this.form?.addEventListener("variant:added", this.onVariantAdded);
      this.moreButton?.addEventListener("click", this.onToggleDetails);
    }

    disconnectedCallback() {
      this.form?.removeEventListener("variant:added", this.onVariantAdded);
      this.moreButton?.removeEventListener("click", this.onToggleDetails);
    }

    handleToggleDetails() {
      if (!this.moreButton) return;

      const expanded = this.moreButton.getAttribute("aria-expanded") === "true";
      this.moreButton.setAttribute("aria-expanded", String(!expanded));
      if (this.details) this.details.hidden = expanded;
      if (this.moreLabel) {
        this.moreLabel.textContent = expanded
          ? this.moreButton.dataset.moreLabel || ""
          : this.moreButton.dataset.lessLabel || "";
      }
    }

    async handleVariantAdded() {
      if (!this.checkbox?.checked || this.addInFlight) return;

      const variantId = Number(this.dataset.variantId);
      if (!variantId) return;

      this.addInFlight = true;
      try {
        if (await this.isAlreadyInCart(variantId)) {
          this.setStatus(this.dataset.addedText || "");
          return;
        }

        const response = await fetch(this.cartAddUrl(), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: ["mini-cart"],
          }),
        });
        const responseJson = await response.json();
        if (!response.ok) {
          throw new Error(responseJson.description || "Graphic check add failed");
        }

        // Same refresh contract as hypemat-cart-upsell.js: hand the fresh cart
        // (with the re-rendered mini-cart section) to the theme's listeners.
        const cartResponse = await fetch(this.cartJsonUrl());
        const cart = await cartResponse.json();
        if (responseJson.sections) cart.sections = responseJson.sections;

        document.documentElement.dispatchEvent(
          new CustomEvent("cart:updated", { bubbles: true, detail: { cart } })
        );
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:refresh", { bubbles: true, detail: { cart } })
        );

        this.setStatus(this.dataset.addedText || "");
      } catch (error) {
        console.error("Hypemat graphic check: add failed", error);
        this.setStatus(this.dataset.errorText || "", true);
      } finally {
        this.addInFlight = false;
      }
    }

    async isAlreadyInCart(variantId) {
      try {
        const cartResponse = await fetch(this.cartJsonUrl());
        const cart = await cartResponse.json();
        return Array.isArray(cart.items) && cart.items.some((item) => Number(item.variant_id) === variantId);
      } catch (error) {
        // Cart unreadable — attempt the add rather than silently dropping a paid service.
        return false;
      }
    }

    setStatus(text, isError = false) {
      if (!this.status) return;
      this.status.textContent = text;
      this.status.hidden = !text;
      this.status.classList.toggle("hypemat-graphic-check__status--error", isError);
    }

    cartAddUrl() {
      const cartAddUrl = window.themeVariables?.routes?.cartAddUrl;
      const root = window.Shopify?.routes?.root || "/";
      return cartAddUrl ? `${cartAddUrl}.js` : `${root}cart/add.js`;
    }

    cartJsonUrl() {
      const cartUrl = window.themeVariables?.routes?.cartUrl;
      const root = window.Shopify?.routes?.root || "/";
      return cartUrl ? `${cartUrl}.js` : `${root}cart.js`;
    }
  }

  customElements.define("hypemat-graphic-check", HypematGraphicCheck);
})();
